/**
 * Activity Logger — records all BMaps actions for session history.
 * Subscribes to CmdInterpreter events via EventBroker.
 * Entirely client-side, stored in memory (not localStorage).
 *
 * @module gibbs/activityLog
 */

import { EventBroker } from '../eventbroker';

const MAX_ENTRIES = 500;
let log = [];
let initialized = false;

/**
 * Initialize the activity log by subscribing to EventBroker events.
 * Safe to call multiple times — only subscribes once.
 */
export function initActivityLog() {
    if (initialized) return;
    // Subscribe to action completion events
    EventBroker.subscribe('actionCompleted', (_evt, data) => {
        addActivity(
            'action',
            data.actionId,
            data.params,
            data.result
        );
    });
    // Subscribe to workspace state changes
    EventBroker.subscribe('compoundLoaded', (_evt, data) => {
        addActivity(
            'event',
            'compound-loaded',
            { name: data?.name }
        );
    });
    EventBroker.subscribe('proteinLoaded', (_evt, data) => {
        addActivity(
            'event',
            'protein-loaded',
            { pdbId: data?.pdbId }
        );
    });
    initialized = true;
}

/**
 * Add an activity entry to the in-memory log.
 *
 * @param {string} type - 'action', 'event', 'gibbs', or 'error'
 * @param {string} action - Action identifier
 * @param {Object} [params={}] - Action parameters (summarized)
 * @param {*} [result=null] - Action result (summarized)
 */
export function addActivity(type, action, params = {}, result = null) {
    log.push({
        timestamp: new Date().toISOString(),
        type,
        action,
        params: summarizeParams(params),
        result: summarizeResult(result),
    });
    if (log.length > MAX_ENTRIES) log = log.slice(-MAX_ENTRIES);
}

/**
 * Get the full activity log (copy).
 * @returns {Array<Object>}
 */
export function getActivityLog() { return [...log]; }

/**
 * Get the most recent N activity entries.
 * @param {number} [count=10]
 * @returns {Array<Object>}
 */
export function getRecentActivity(count = 10) {
    return log.slice(-count);
}

/**
 * Clear all activity log entries.
 */
export function clearActivityLog() { log = []; }

/**
 * Summarize params to avoid storing huge objects.
 * @param {*} params
 * @returns {*}
 * @private
 */
function summarizeParams(params) {
    if (!params || typeof params !== 'object') return params;
    const summary = {};
    for (const [k, v] of Object.entries(params)) {
        if (typeof v === 'string') {
            summary[k] = v.length > 100
                ? `${v.slice(0, 100)}...`
                : v;
        } else if (
            typeof v === 'number'
            || typeof v === 'boolean'
        ) {
            summary[k] = v;
        } else if (Array.isArray(v)) {
            summary[k] = `[${v.length} items]`;
        } else {
            summary[k] = '[object]';
        }
    }
    return summary;
}

/**
 * Summarize a result value for storage.
 * @param {*} result
 * @returns {*}
 * @private
 */
function summarizeResult(result) {
    if (!result) return null;
    if (typeof result === 'string') return result.slice(0, 200);
    if (result.type) return { type: result.type };
    return '[result]';
}

// ─── Session Export (Feature 2) ─────────────────────────────────────────────

/**
 * Export the session activity log as a Markdown string.
 * @returns {string}
 */
export function exportSessionAsMarkdown() {
    const lines = [
        '# BMaps Session Log',
        `> Date: ${new Date().toISOString()}`,
        `> Activities: ${log.length}`,
        '',
    ];
    for (const entry of log) {
        const time = entry.timestamp.split('T')[1].split('.')[0];
        const params = entry.params
            ? ` — ${JSON.stringify(entry.params)}`
            : '';
        lines.push(`- [${time}] **${entry.action}**${params}`);
    }
    return lines.join('\n');
}

/**
 * Download the session log as a Markdown file.
 */
export function downloadSessionLog() {
    const md = exportSessionAsMarkdown();
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().split('T')[0];
    a.download = `bmaps-session-${date}.md`;
    a.click();
    URL.revokeObjectURL(url);
}

// ─── Quick-Action Shortcuts (Feature 4) ─────────────────────────────────────

/**
 * Get frequently used actions from the activity log.
 *
 * @param {number} [minCount=3] - Minimum occurrence count
 * @returns {Array<{action: string, count: number}>}
 */
export function getFrequentActions(minCount = 3) {
    const counts = {};
    for (const entry of log) {
        if (entry.type === 'action' && entry.action) {
            counts[entry.action] = (counts[entry.action] || 0) + 1;
        }
    }
    return Object.entries(counts)
        .filter(([, count]) => count >= minCount)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([action, count]) => ({ action, count }));
}

/**
 * Reset module state. Intended for testing only.
 * @private
 */
export function resetForTesting() {
    log = [];
    initialized = false;
}
